import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useNotifications } from '../../contexts/NotificationContext'
import { useHaptics } from '../../utils/haptics'
import { useMoney } from '../../hooks/usePublicSettings'
import { bookingApi, moneyRange, callOutCreditText } from './bookingApi'
import StatusIndicator from '../common/StatusIndicator'

// Icons
function ArrowLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  )
}

function StarIcon({ filled = false, ...props }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
    </svg>
  )
}

function MapPinIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  )
}

function PhoneIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  )
}

function MessageCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  )
}

function CheckCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M9 11l3 3L22 4"/>
    </svg>
  )
}

export default function ProfessionalProfile() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { showError } = useNotifications()
  const haptics = useHaptics()
  const money = useMoney()
  const [professional, setProfessional] = useState(null)
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  useEffect(() => {
    loadProfessionalData()
  }, [id])

  const loadProfessionalData = async () => {
    try {
      // Profile + published reviews from the server (professional_reviews)
      const data = await bookingApi.professional(id)
      setProfessional(data)
      setReviews(data.reviews || [])
    } catch (error) {
      console.error('Error loading professional:', error)
      setProfessional(null)
      setReviews([])
      showError('Failed to load professional details')
    } finally {
      setLoading(false)
    }
  }

  const handleBookNow = () => {
    haptics.light()
    // Booking context from where the profile was opened (AI chat card, booking review): trade, severity, method
    navigate(`/booking/datetime/${professional.id}`, { state: location.state || {} })
  }

  const handleCall = () => {
    haptics.light()
    window.location.href = `tel:${professional.phone}`
  }

  const handleMessage = () => {
    haptics.light()
    // ✅ FIX: Navigate to messaging screen instead of opening WhatsApp
    navigate(`/messages/${professional.id}`, {
      state: {
        professional: {
          id: professional.id,
          name: professional.name,
          company: professional.company,
          avatar: professional.avatar
        }
      }
    })
  }

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon key={i} filled={i < Math.floor(rating)} className="text-yellow-400" />
    ))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    )
  }

  if (!professional) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Professional not found</h2>
          <button 
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900"
          >
            Go back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white px-4 py-4 shadow-lg">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-xl touch-manipulation"
          >
            <ArrowLeftIcon />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Professional Profile</h1>
            <p className="text-sm text-slate-500">{professional.company}</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6 pb-24">
        {/* Professional Info Card */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
              <span className="text-xl font-semibold text-slate-600">
                {professional.name ? professional.name.split(' ').map(n => n[0]).join('') : 'N/A'}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-bold text-slate-900">{professional.name}</h2>
                <StatusIndicator 
                  status={professional.onlineStatus} 
                  responseTime={professional.responseTime}
                  size="md"
                  showText={true}
                  showResponseTime={false}
                />
                {professional.verified && (
                  <CheckCircleIcon className="text-green-600" />
                )}
              </div>
              <p className="text-sm font-medium text-slate-700">{professional.company}</p>
              <div className="flex items-center gap-1 mt-1">
                {renderStars(professional.rating || 0)}
                <span className="text-sm font-medium text-slate-900 ml-1">
                  {professional.rating != null ? Number(professional.rating).toFixed(1) : '—'}
                </span>
                <span className="text-sm text-slate-500">
                  ({professional.reviewCount} reviews)
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-slate-500">Experience</div>
              <div className="font-medium text-slate-900">{professional.yearsExperience != null ? `${professional.yearsExperience} years` : '—'}</div>
            </div>
            <div>
              <div className="text-slate-500">Completed Jobs</div>
              <div className="font-medium text-slate-900">{professional.completedJobs}</div>
            </div>
            <div>
              <div className="text-slate-500">Hourly Rate</div>
              <div className="font-medium text-slate-900">{professional.hourlyRate ? `${moneyRange(money, professional.hourlyRate)}/hour` : '—'}</div>
            </div>
            <div>
              <div className="text-slate-500">Call-out fee</div>
              <div className="font-medium text-slate-900">{money(professional.callOut?.standard, { currency: professional.currency })}</div>
            </div>
          </div>

          {/* Call-out by case (standard hours; the chosen time's rate applies at booking) */}
          {professional.callOut && (
            <div className="mt-3 text-xs text-slate-500 space-y-0.5">
              <div>
                Severe issue {money(professional.callOut.severe, { currency: professional.currency })} • Emergency {money(professional.callOut.emergency, { currency: professional.currency })}
              </div>
              <div className={professional.callOut.deductible ? 'text-green-600' : ''}>{callOutCreditText(professional.callOut.deductible)}</div>
            </div>
          )}

          <div className="flex items-center gap-2 mt-4 text-sm text-slate-600">
            <MapPinIcon />
            <span>{[professional.location, professional.maxTravelDistanceKm != null && `travels up to ${professional.maxTravelDistanceKm} km`].filter(Boolean).join(' • ') || '—'}</span>
          </div>
        </div>

        {/* Specialties */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-3">Specialties</h3>
          <div className="flex flex-wrap gap-2">
            {(professional.trades || []).map((specialty, index) => (
              <span 
                key={index}
                className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium"
              >
                {specialty}
              </span>
            ))}
          </div>
        </div>

        {/* About */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-3">About</h3>
          <p className="text-slate-700 leading-relaxed">{professional.bio || '—'}</p>
        </div>

        {/* Portfolio (professional_projects) */}
        {(professional.portfolio || []).length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-3">Recent Work</h3>
          <div className="grid grid-cols-2 gap-3">
            {(professional.portfolio || []).map((item, index) => (
              <div 
                key={item.id}
                onClick={() => setSelectedImageIndex(index)}
                className="aspect-square bg-slate-100 rounded-xl overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
              >
                {item.image ? (
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
                    {item.title}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Reviews */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-3">Recent Reviews</h3>
          <div className="space-y-4">
            {reviews.length === 0 && <p className="text-sm text-slate-500">No reviews yet</p>}
            {(reviews || []).map((review) => (
              <div key={review.id} className="border-b border-slate-100 last:border-b-0 pb-4 last:pb-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{review.customerName || 'Anonymous'}</span>
                    <div className="flex items-center gap-1">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  <span className="text-xs text-slate-500">{review.date ? new Date(review.date).toLocaleDateString() : ''}</span>
                </div>
                <p className="text-sm text-slate-700 mb-1">{review.comment}</p>
                {review.service && <span className="text-xs text-slate-500">Service: {review.service}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Availability - Real data from database */}
        {/* ✅ FIX: Availability shows booking slots, activeJobs shows current workload
            - "Today/Tomorrow/This Week" = Whether they have open booking slots
            - "Active Jobs" = Current jobs they're working on
            - A professional can have active jobs but still accept new bookings */}
        <div className="bg-white rounded-2xl p-5 shadow-lg">
          <h3 className="font-semibold text-slate-900 mb-3">Availability</h3>
          {professional.availability ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className={`p-3 rounded-xl text-center ${
                  professional.availability.today 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-slate-50 text-slate-500'
                }`}>
                  <div className="text-sm font-medium">Today</div>
                  <div className="text-xs">
                    {professional.availability.today ? 'Open Slots' : 'Fully Booked'}
                  </div>
                </div>
                <div className={`p-3 rounded-xl text-center ${
                  professional.availability.tomorrow 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-slate-50 text-slate-500'
                }`}>
                  <div className="text-sm font-medium">Tomorrow</div>
                  <div className="text-xs">
                    {professional.availability.tomorrow ? 'Open Slots' : 'Fully Booked'}
                  </div>
                </div>
                <div className={`p-3 rounded-xl text-center ${
                  professional.availability.thisWeek 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-slate-50 text-slate-500'
                }`}>
                  <div className="text-sm font-medium">This Week</div>
                  <div className="text-xs">
                    {professional.availability.thisWeek ? 'Open Slots' : 'Limited'}
                  </div>
                </div>
              </div>
              
              {/* Show active jobs count if available */}
              {professional.activeJobs !== undefined && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="text-xs text-slate-600">
                    <span className="font-medium">Current Workload: </span>
                    {professional.activeJobs === 0 ? (
                      <span className="text-green-600">No active jobs</span>
                    ) : (
                      <span>{professional.activeJobs} active job{professional.activeJobs > 1 ? 's' : ''} in progress</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {professional.availability.today || professional.availability.tomorrow || professional.availability.thisWeek
                      ? '✓ Accepting new bookings'
                      : 'Schedule is currently full'}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-slate-500">
              Availability information not available
            </div>
          )}
        </div>
      </div>

      {/* Bottom Action Bar - ✅ FIX: Removed Call button, kept only Message and Book Now */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 safe-area-inset">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex gap-3">
            <button
              onClick={handleMessage}
              disabled
              title="Not available yet"
              className="disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 px-6 py-3 border-2 border-slate-300 rounded-xl text-slate-700 font-medium hover:bg-slate-50 touch-manipulation transition-colors"
            >
              <MessageCircleIcon />
              Message
            </button>
            <button
              onClick={handleBookNow}
              className="flex-1 py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 touch-manipulation transition-colors shadow-md"
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
